import type { ComponentType } from "react"
import type { QuizAnswers, QuizStep } from "../../types"

/** The value a single step reads/writes in the answers store. */
export type QuizAnswerValue = string | string[]

/**
 * Uniform props every step component receives. Concrete steps only use the
 * subset they need; the renderer always passes the full set so the registry
 * can stay generic.
 */
export interface StepComponentProps {
  step: QuizStep
  value: QuizAnswerValue | undefined
  /** Persist the answer for this step. */
  onChange: (value: QuizAnswerValue) => void
  /** Request moving to the next step (e.g. an "analysis" step's Continue). */
  onAdvance: () => void
  /** Go back one step — for full-bleed steps that render their own navbar. */
  onBack: () => void
  /** Counted progress (for full-bleed steps that render their own quiz header). */
  countedCurrent: number
  countedTotal: number
  /** All answers collected so far — for steps that read sibling answers. */
  answers: QuizAnswers
}

/**
 * Registry entry describing how a step type renders and validates. Adding a new
 * step type means adding one descriptor here — `QuizRenderer` and `use-quiz`
 * stay untouched.
 */
export interface StepDescriptor {
  Component: ComponentType<StepComponentProps>
  /** Whether the step can be left (gates the Continue button). Defaults to allowed. */
  validate?: (value: QuizAnswerValue | undefined, step: QuizStep) => boolean
  /** Single-choice-style steps that advance on selection (no Continue button). */
  autoAdvance?: boolean
  /** Render the component on its own, without the header/title chrome (e.g. analysis). */
  fullBleed?: boolean
}

/** Shared "has a usable value" check used by most validators. */
export function isFilled(value: QuizAnswerValue | undefined): boolean {
  if (!value) return false
  if (Array.isArray(value)) return value.length > 0
  return value.trim() !== ""
}

/**
 * Validate a free-text value against the step's configured `validation.pattern`
 * (matched on the trimmed string). Falls back to a plain "is filled" check when
 * the step declares no pattern, so the rule stays fully data-driven.
 */
export function matchesPattern(value: QuizAnswerValue | undefined, step: QuizStep): boolean {
  if (!step.validation) return isFilled(value)
  if (typeof value !== "string") return false
  return new RegExp(step.validation.pattern, step.validation.flags ?? "u").test(value.trim())
}
